import { isBlankHtml } from "../html";

export const SUPPORTED_LOCALES = ["en", "fr", "es"] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = "en";

export const DEFAULT_COMPANY_LOCALE = "en-US";

export const SUPPORTED_LOCALE_LABELS: Record<SupportedLocale, string> = {
	en: "English",
	fr: "Français",
	es: "Español",
};

type LocaleUnionsMatch<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
const _prismaJsonLocalesMatchDomain: LocaleUnionsMatch<keyof PrismaJson.CompanySupportI18n, SupportedLocale> = true;
const _prismaJsonCustomerSettingsLocalesMatchDomain: LocaleUnionsMatch<
	keyof PrismaJson.CompanyTerminologyOverrides,
	SupportedLocale
> = true;

const supportedLocaleSet = new Set<string>(SUPPORTED_LOCALES);

const isSupportedLocale = (locale: string): locale is SupportedLocale => supportedLocaleSet.has(locale);

export function parseAcceptLanguage(header: string | null | undefined): SupportedLocale | undefined {
	if (!header) {
		return undefined;
	}

	const candidates = header
		.split(",")
		.map((value, index) => {
			const [tag, ...parameters] = value.split(";").map((part) => part.trim());
			let quality = 1;

			if (!tag || tag === "*" || parameters.length > 1) {
				return undefined;
			}

			if (parameters.length === 1) {
				const qualityMatch = parameters[0]?.match(/^q\s*=\s*(.+)$/i);
				if (!qualityMatch) {
					return undefined;
				}

				const qualityValue = qualityMatch[1];
				if (!qualityValue || !/^(?:0(?:\.\d{0,3})?|1(?:\.0{0,3})?)$/.test(qualityValue)) {
					return undefined;
				}

				quality = Number(qualityValue);
			}

			if (quality === 0) {
				return undefined;
			}

			return {
				locale: tag.split("-")[0]?.toLowerCase() ?? "",
				quality,
				index,
			};
		})
		.filter((candidate): candidate is NonNullable<typeof candidate> => candidate !== undefined)
		.sort((a, b) => b.quality - a.quality || a.index - b.index);

	for (const candidate of candidates) {
		if (isSupportedLocale(candidate.locale)) {
			return candidate.locale;
		}
	}

	return undefined;
}

export function toSupportedLocale(locale: string | null | undefined): SupportedLocale | undefined {
	return locale != null && isSupportedLocale(locale) ? locale : undefined;
}

export function toShortLocale(locale: string): SupportedLocale {
	const shortLocale = locale.split("-")[0]?.toLowerCase() ?? "";
	return isSupportedLocale(shortLocale) ? shortLocale : DEFAULT_LOCALE;
}

export function effectiveSupportedLocales(company: {
	locale: string;
	supportedLocales?: SupportedLocale[] | null;
}): SupportedLocale[] {
	const defaultLocale = toShortLocale(company.locale);
	const supportedLocales = [...new Set(company.supportedLocales ?? [])];

	return supportedLocales.includes(defaultLocale) ? supportedLocales : [defaultLocale, ...supportedLocales];
}

export function resolveRecipientLocale(args: {
	taskLocale?: string | null;
	entityLocale?: string | null;
	company: { locale: string; supportedLocales?: SupportedLocale[] | null };
}): SupportedLocale {
	const supportedLocales = effectiveSupportedLocales(args.company);
	const candidate = [args.taskLocale, args.entityLocale].find(
		(locale): locale is SupportedLocale =>
			typeof locale === "string" && isSupportedLocale(locale) && supportedLocales.includes(locale),
	);

	return candidate ?? toShortLocale(args.company.locale);
}

export function resolveTranslatedField<F extends string>(args: {
	base: string | null | undefined;
	i18n: Partial<Record<SupportedLocale, Partial<Record<F, string>>>> | null | undefined;
	field: F;
	locale: SupportedLocale;
	companyDefaultLocale: SupportedLocale;
}): string | null | undefined {
	if (args.locale === args.companyDefaultLocale) {
		return args.base;
	}

	const translatedValue = args.i18n?.[args.locale]?.[args.field];
	return translatedValue && !isBlankHtml(translatedValue) ? translatedValue : args.base;
}
